/**
 * This module defines custom PropTypes shared within the application.
 *
 * To learn about validating React component props with PropTypes, see:
 *
 *     https://facebook.github.io/react/docs/typechecking-with-proptypes.html
 *
 * For component specific PropTypes, it's perfectly ok to inline them
 * to the component itself. If the type is shared or external (SDK or
 * API), however, it should be in this file for sharing with other
 * components.
 *
 * PropTypes should usually be validated only at the lowest level
 * where the props are used, not along the way in parents that pass
 * along the props to their children. Those parents should usually
 * just validate the presense of the prop key and that the value is
 * defined. This way we get the validation errors only in the most
 * specific place and avoid duplicate errros.
 */
import * as sdkTypes from '@vansite/ts-sharetribe-flex-sdk/types';
import {
  any,
  arrayOf,
  bool,
  func,
  instanceOf,
  node,
  number,
  object,
  objectOf,
  oneOf,
  oneOfType,
  shape,
  string,
} from 'prop-types';

import { TRANSITIONS as DEFAULT_TRANSITIONS, TX_TRANSITION_ACTORS } from './transaction.default';
import { TRANSITIONS as DELAYED_TRANSITIONS } from './transaction.delayed';

const legacyTransitions = ['transition/create-stripe-payment-intent'];

const ALL_TRANSITIONS = [
  ...new Set([...DEFAULT_TRANSITIONS, ...DELAYED_TRANSITIONS, ...legacyTransitions]),
];

const { UUID, LatLng, LatLngBounds, Money } = sdkTypes;

const propTypes = {};

propTypes.arrayOf = arrayOf;
propTypes.bool = bool;
propTypes.func = func;
propTypes.instanceOf = instanceOf;
propTypes.node = node;
propTypes.number = number;
propTypes.object = object;
propTypes.objectOf = objectOf;
propTypes.oneOf = oneOf;
propTypes.oneOfType = oneOfType;
propTypes.shape = shape;
propTypes.string = string;

// Fixed value
propTypes.value = (val) => oneOf([val]);

// SDK type instances
propTypes.uuid = instanceOf(UUID);
propTypes.latlng = instanceOf(LatLng);
propTypes.latlngBounds = instanceOf(LatLngBounds);
propTypes.money = instanceOf(Money);

propTypes.userRole = oneOf(['customer', 'provider']);

// Configuration for currency formatting
propTypes.currencyConfig = shape({
  style: string.isRequired,
  currency: string.isRequired,
  currencyDisplay: string,
  useGrouping: bool,
  minimumFractionDigits: number,
  maximumFractionDigits: number,
});

// Configuration for a single route
propTypes.route = shape({
  name: string,
  path: string.isRequired,
  exact: bool,
  strict: bool,
  component: oneOfType([object, func]).isRequired,
  loadDuck: func,
});

// Place object from LocationAutocompleteInput
propTypes.place = shape({
  address: string.isRequired,
  origin: propTypes.latlng,
  bounds: propTypes.latlngBounds, // optional viewport bounds
});

// Denormalised image object
propTypes.image = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('image').isRequired,
  attributes: shape({
    variants: objectOf(
      shape({
        width: number.isRequired,
        height: number.isRequired,
        url: string.isRequired,
      }),
    ),
  }),
});

propTypes.annualPass = shape({
  active: bool.isRequired,
  code: string.isRequired,
  dateOfPublication: string.isRequired,
  validUntil: string.isRequired,
  twentyFivePass: bool,
  flexPass: bool,
  rentalStartDate: string,
  rentalEndDate: string,
  included: shape({
    guestCount: number.isRequired,
    extraLineItems: arrayOf(string),
    plus: bool,
  }),
});

const currentUserPrivateData = shape({
  annualPass: propTypes.annualPass,
  bookmarks: arrayOf(string),
  language: string,
  lastPolicyAcknowledged: string,
  mandate: string,
  paymentMethod: object,
  stripeCustomer: object,
  voucherifyCustomer: shape({
    id: string,
  }),
});

const currentUserProtectedData = shape({
  annualPassLicensePlate: string,
  comingFrom: string,
  comingFromDetail: string,
  phoneNumber: string,
});

const currentUserProfile = shape({
  firstName: string.isRequired,
  lastName: string.isRequired,
  displayName: string.isRequired,
  abbreviatedName: string.isRequired,
  bio: string,
  publicData: shape({
    travelDetails: shape({
      vehicle: oneOf(['van', 'bus', 'camper', 'caravan', 'motorhome', 'carTent', 'tent', '']),
    }),
    spokenLanguages: arrayOf(string),
  }),
  privateData: currentUserPrivateData,
  protectedData: currentUserProtectedData,
});

const currentUserAttributes = shape({
  banned: bool.isRequired,
  email: string.isRequired,
  emailVerified: bool.isRequired,
  profile: currentUserProfile.isRequired,
  stripeConnected: bool,
});

// Denormalised user object
propTypes.currentUser = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('currentUser').isRequired,
  attributes: currentUserAttributes,
  profileImage: propTypes.image,
});

const userAttributes = shape({
  banned: propTypes.value(false).isRequired,
  deleted: propTypes.value(false).isRequired,
  profile: shape({
    displayName: string.isRequired,
    abbreviatedName: string.isRequired,
    bio: string,
    publicData: object,
  }),
});

// Listing queries can include author.
// Banned and deleted are not relevant then
// since banned and deleted users can't have listings.
const authorAttributes = shape({
  profile: shape({
    displayName: string.isRequired,
    abbreviatedName: string.isRequired,
    bio: string,
  }),
});

const deletedUserAttributes = shape({
  deleted: propTypes.value(true).isRequired,
});

const bannedUserAttributes = shape({
  banned: propTypes.value(true).isRequired,
});

// Denormalised user object
propTypes.user = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('user').isRequired,
  attributes: oneOfType([
    userAttributes,
    authorAttributes,
    deletedUserAttributes,
    bannedUserAttributes,
  ]).isRequired,
  profileImage: propTypes.image,
});

export const LISTING_STATE_DRAFT = 'draft';
export const LISTING_STATE_PENDING_APPROVAL = 'pendingApproval';
export const LISTING_STATE_PUBLISHED = 'published';
export const LISTING_STATE_CLOSED = 'closed';

const LISTING_STATES = [
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
  LISTING_STATE_CLOSED,
];

const sharedTimes = [
  '09_clock',
  '10_clock',
  '11_clock',
  '12_clock',
  '13_clock',
  '14_clock',
  '15_clock',
  '16_clock',
  '17_clock',
  '18_clock',
  '19_clock',
  '20_clock',
  '21_clock',
];

const earliestArrivalTime = oneOf(['08_clock', ...sharedTimes]);
const latestArrivalTime = oneOf([...sharedTimes, '22_clock']);
const latestDepartureTime = oneOf([
  '08_clock',
  '09_clock',
  '10_clock',
  '11_clock',
  '12_clock',
  '13_clock',
  '14_clock',
  '15_clock',
]);

propTypes.customLineItem = shape({
  description: string.isRequired,
  price: shape({
    amount: number.isRequired,
    currency: string.isRequired,
  }).isRequired,
  title: string.isRequired,
  type: shape({
    perGuest: bool.isRequired,
    perItem: bool.isRequired,
    perNight: bool.isRequired,
  }),
});

propTypes.extraLineItem = shape({
  price: shape({ amount: number.isRequired, currency: string.isRequired }),
  type: oneOf([shape({ perNight: bool.isRequired }), shape({ perGuest: bool.isRequired })]),
});

propTypes.customLineItemTranslation = shape({
  title: string.isRequired,
  description: string.isRequired,
});

propTypes.cancellationPolicy = shape({
  bottomLimit: number,
  middleLimit: number,
  topLimit: number,
});

const listingAttributesPublicData = shape({
  activities: arrayOf(string),
  allWheelDrive: oneOf(['yes', 'no']),
  amenities: arrayOf(string),
  amountOfSeats: number,
  bookingLimit: oneOf(['limit_1', 'limit_2', 'limit_3', 'limit_4', 'limit_bigger_4']),
  cancellationPolicy: propTypes.cancellationPolicy,
  category: oneOf(['bookingRequest', 'expressBooking']),
  customLineItems: objectOf(propTypes.customLineItem),
  earliestArrivalTime: earliestArrivalTime,
  expressBookingConsent: bool,
  extraLineItems: objectOf(propTypes.extraLineItem),
  guestOptions: shape({
    guestCountIncluded: number,
    maxGuestCount: number,
    pricePerExtraGuest: number,
  }),
  hidden: bool,
  imagesMetadata: arrayOf(
    shape({
      id: propTypes.string,
      surroundImage: propTypes.bool,
    }),
  ),
  kfz: arrayOf(string),
  latestArrivalTime: latestArrivalTime,
  latestDepartureTime: latestDepartureTime,
  location: shape({
    address: string,
    country: string,
  }),
  locationPlace: string,
  rules: string,
  surroundings: arrayOf(string),
  verified: oneOf([0, 1]),
});

const listingAttributesMetadata = shape({
  rating: number,
  ratingCount: number,
});

const listingAttributes = shape({
  title: string.isRequired,
  description: string,
  geolocation: propTypes.latlng,
  deleted: propTypes.value(false),
  state: oneOf(LISTING_STATES),
  price: propTypes.money,
  //publicData: listingAttributesPublicData,
  //metadata: listingAttributesMetadata,
});

const AVAILABILITY_PLAN_DAY = 'availability-plan/day';
const AVAILABILITY_PLAN_TIME = 'availability-plan/time';
export const DAYS_OF_WEEK = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const availabilityPlan = shape({
  type: oneOf([AVAILABILITY_PLAN_DAY, AVAILABILITY_PLAN_TIME]).isRequired,
  timezone: string,
  entries: arrayOf(
    shape({
      dayOfWeek: oneOf(DAYS_OF_WEEK).isRequired,
      seats: number.isRequired,
      start: string,
      end: string,
    }),
  ),
});

propTypes.availabilityPlan = availabilityPlan;

const ownListingAttributes = shape({
  title: string.isRequired,
  description: string,
  geolocation: propTypes.latlng,
  deleted: propTypes.value(false).isRequired,
  state: oneOf(LISTING_STATES).isRequired,
  price: propTypes.money,
  availabilityPlan: availabilityPlan,
  publicData: listingAttributesPublicData.isRequired,
  metadata: listingAttributesMetadata.isRequired,
});

const deletedListingAttributes = shape({
  deleted: propTypes.value(true).isRequired,
});

// Denormalised listing object
propTypes.listing = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('listing').isRequired,
  attributes: oneOfType([listingAttributes, deletedListingAttributes]).isRequired,
  author: propTypes.user,
  images: arrayOf(propTypes.image),
});

// Denormalised ownListing object
propTypes.ownListing = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('ownListing').isRequired,
  attributes: oneOfType([ownListingAttributes, deletedListingAttributes]).isRequired,
  author: propTypes.currentUser,
  images: arrayOf(propTypes.image),
});

export const BOOKING_STATE_PENDING = 'pending';
export const BOOKING_STATE_ACCEPTED = 'accepted';
export const BOOKING_STATE_DECLINED = 'declined';
export const BOOKING_STATE_CANCELLED = 'cancelled';
export const BOOKING_STATES = [
  BOOKING_STATE_PENDING,
  BOOKING_STATE_ACCEPTED,
  BOOKING_STATE_DECLINED,
  BOOKING_STATE_CANCELLED,
];

// Denormalised booking object
propTypes.booking = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('booking').isRequired,
  attributes: shape({
    end: instanceOf(Date).isRequired,
    start: instanceOf(Date).isRequired,
    displayStart: instanceOf(Date),
    displayEnd: instanceOf(Date),
    state: oneOf(BOOKING_STATES),
  }),
});

// A time slot that covers one day, having a start and end date.
export const TIME_SLOT_DAY = 'time-slot/day';

// Denormalised time slot object
propTypes.timeSlot = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('timeSlot').isRequired,
  attributes: shape({
    type: oneOf([TIME_SLOT_DAY]).isRequired,
    end: instanceOf(Date).isRequired,
    start: instanceOf(Date).isRequired,
  }),
});

// Denormalised availability exception object
propTypes.availabilityException = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('availabilityException').isRequired,
  attributes: shape({
    end: instanceOf(Date).isRequired,
    seats: number.isRequired,
    start: instanceOf(Date).isRequired,
  }),
});

propTypes.transition = shape({
  createdAt: instanceOf(Date).isRequired,
  by: oneOf(TX_TRANSITION_ACTORS).isRequired,
  transition: oneOf(ALL_TRANSITIONS).isRequired,
});

// Possible amount of stars in a review
export const REVIEW_RATINGS = [1, 2, 3, 4, 5];

// Review types: review of a provider and of a customer
export const REVIEW_TYPE_OF_PROVIDER = 'ofProvider';
export const REVIEW_TYPE_OF_CUSTOMER = 'ofCustomer';

// A review on a user
propTypes.review = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('review').isRequired,
  attributes: shape({
    createdAt: instanceOf(Date).isRequired,
    content: string,
    rating: oneOf(REVIEW_RATINGS),
    state: string.isRequired,
    type: oneOf([REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER]).isRequired,
  }).isRequired,
  author: propTypes.user,
  subject: propTypes.user,
});

// A Stripe account entity
propTypes.stripeAccount = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('stripeAccount').isRequired,
  attributes: shape({
    stripeAccountId: string.isRequired,
    stripeAccountData: object,
  }),
});

propTypes.defaultPaymentMethod = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('stripePaymentMethod').isRequired,
  attributes: shape({
    type: propTypes.value('stripe-payment-method/card').isRequired,
    stripePaymentMethodId: string.isRequired,
    card: shape({
      brand: string.isRequired,
      expirationMonth: number.isRequired,
      expirationYear: number.isRequired,
      last4Digits: string.isRequired,
    }).isRequired,
  }),
});

export const LINE_ITEM_NIGHT = 'line-item/night';
export const LINE_ITEM_DAY = 'line-item/day';
export const LINE_ITEM_UNITS = 'line-item/units';
export const LINE_ITEM_CUSTOMER_COMMISSION = 'line-item/customer-commission';
export const LINE_ITEM_CUSTOMER_COMMISSION_EXTRA_LINE_ITEMS =
  'line-item/customer-commission-extra-line-items';
export const LINE_ITEM_PROVIDER_COMMISSION = 'line-item/provider-commission';
export const LINE_ITEM_PROVIDER_NIGHTLY_COMMISSION = 'line-item/provider-nightly-commission';
export const LINE_ITEM_VOUCHER = 'line-item/voucher';
export const LINE_ITEM_ANNUAL_PASS_VOUCHER = 'line-item/annual-pass-voucher';
export const LINE_ITEM_TRANSACTION_COMPENSATION = 'line-item/transaction-compensation';
export const LINE_ITEM_EXTRA_GUEST = 'line-item/extra-guest';
export const LINE_ITEM_EXTRA_ADULT = 'line-item/extra-adult';
export const LINE_ITEM_EXTRA_CHILD = 'line-item/extra-child';
export const LINE_ITEM_EXTRA_TODDLER = 'line-item/extra-toddler';
export const LINE_ITEM_EXTRA_PET = 'line-item/extra-pet';
export const LINE_ITEM_DIGITAL_GIFT_CARD = 'line-item/digital-gift-card';
export const LINE_ITEM_LISTING_CONTINGENT = 'line-item/listing-contingent';
export const EXTRA_LINE_ITEM_ELECTRICITY = 'line-item/electricity';
export const EXTRA_LINE_ITEM_WATER = 'line-item/water';
export const EXTRA_LINE_ITEM_SHOWER = 'line-item/shower';
export const EXTRA_LINE_ITEM_WC = 'line-item/wc';

export const LINE_ITEMS = [
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_UNITS,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_CUSTOMER_COMMISSION_EXTRA_LINE_ITEMS,
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_PROVIDER_NIGHTLY_COMMISSION,
  LINE_ITEM_VOUCHER,
  LINE_ITEM_TRANSACTION_COMPENSATION,
  LINE_ITEM_ANNUAL_PASS_VOUCHER,
  LINE_ITEM_DIGITAL_GIFT_CARD,
  LINE_ITEM_LISTING_CONTINGENT,
];

export const NEGATIVE_LINE_ITEMS = [
  LINE_ITEM_VOUCHER,
  LINE_ITEM_TRANSACTION_COMPENSATION,
  LINE_ITEM_ANNUAL_PASS_VOUCHER,
  LINE_ITEM_DIGITAL_GIFT_CARD,
  LINE_ITEM_LISTING_CONTINGENT,
];

export const EXTRA_LINE_ITEMS = [
  LINE_ITEM_EXTRA_GUEST,
  LINE_ITEM_EXTRA_ADULT,
  LINE_ITEM_EXTRA_CHILD,
  LINE_ITEM_EXTRA_TODDLER,
  LINE_ITEM_EXTRA_PET,
  EXTRA_LINE_ITEM_ELECTRICITY,
  EXTRA_LINE_ITEM_WATER,
  EXTRA_LINE_ITEM_SHOWER,
  EXTRA_LINE_ITEM_WC,
];

export const COMMISSION_LINE_ITEMS = [
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_CUSTOMER_COMMISSION_EXTRA_LINE_ITEMS,
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_PROVIDER_NIGHTLY_COMMISSION,
];

export const EXCLUDED_ANNUAL_PASS_LINE_ITEMS = [
  LINE_ITEM_NIGHT,
  LINE_ITEM_CUSTOMER_COMMISSION,
  EXTRA_LINE_ITEM_ELECTRICITY,
  EXTRA_LINE_ITEM_WATER,
  EXTRA_LINE_ITEM_SHOWER,
  EXTRA_LINE_ITEM_WC,
  LINE_ITEM_ANNUAL_PASS_VOUCHER,
];

propTypes.bookingUnitType = oneOf([LINE_ITEM_NIGHT, LINE_ITEM_DAY, LINE_ITEM_UNITS]);

const requiredLineItemPropType = (props, propName, componentName) => {
  const prop = props[propName];

  if (!prop || prop === '') {
    return new Error(`Missing line item code prop from ${componentName}.`);
  }
  if (!/^line-item\/.+/.test(prop)) {
    return new Error(`Invalid line item code value ${prop} passed to ${componentName}.`);
  }

  return null;
};

propTypes.lineItem = shape({
  code: requiredLineItemPropType,
  includeFor: arrayOf(oneOf(['customer', 'provider'])).isRequired,
  quantity: number,
  unitPrice: propTypes.money.isRequired,
  lineTotal: propTypes.money.isRequired,
  reversal: bool.isRequired,
});

// Denormalised transaction object
propTypes.transaction = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('transaction').isRequired,
  attributes: shape({
    createdAt: instanceOf(Date),
    lastTransitionedAt: instanceOf(Date).isRequired,
    lastTransition: oneOf(ALL_TRANSITIONS).isRequired,

    // An enquiry won't need a total sum nor a booking so these are
    // optional.
    payinTotal: propTypes.money,
    payoutTotal: propTypes.money,

    lineItems: arrayOf(propTypes.lineItem),
    transitions: arrayOf(propTypes.transition).isRequired,
  }),
  booking: propTypes.booking,
  listing: propTypes.listing,
  customer: propTypes.user,
  provider: propTypes.user,
  reviews: arrayOf(propTypes.review),
});

// Denormalised transaction message
propTypes.message = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('message').isRequired,
  attributes: shape({
    createdAt: instanceOf(Date).isRequired,
    content: string.isRequired,
  }).isRequired,
  sender: propTypes.user,
});

// Pagination information in the response meta
propTypes.pagination = shape({
  page: number.isRequired,
  perPage: number.isRequired,
  totalItems: number.isRequired,
  totalPages: number.isRequired,
});

// Search filter definition
propTypes.filterConfig = arrayOf(
  shape({
    id: string.isRequired,
    label: node,
    type: string.isRequired,
    group: oneOf(['primary', 'secondary']).isRequired,
    queryParamNames: arrayOf(string).isRequired,
    config: object,
  }).isRequired,
);

propTypes.sortConfig = shape({
  active: bool,
  queryParamName: oneOf(['sort']).isRequired,
  relevanceKey: string.isRequired,
  conflictingFilters: arrayOf(string),
  options: arrayOf(
    shape({
      key: oneOf([
        'createdAt',
        '-createdAt',
        'pub_verified,price',
        'pub_verified,-price',
        'relevance',
        'meta_rating,meta_ratingCount',
        '-meta_rating,meta_ratingCount',
      ]).isRequired,
      label: string.isRequired,
      longLabel: string,
    }),
  ),
});

export const ERROR_CODE_TRANSACTION_LISTING_NOT_FOUND = 'transaction-listing-not-found';
export const ERROR_CODE_TRANSACTION_INVALID_TRANSITION = 'transaction-invalid-transition';
export const ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_CUSTOMER =
  'transaction-already-reviewed-by-customer';
export const ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_PROVIDER =
  'transaction-already-reviewed-by-provider';
export const ERROR_CODE_TRANSACTION_BOOKING_TIME_NOT_AVAILABLE =
  'transaction-booking-time-not-available';
export const ERROR_CODE_PAYMENT_FAILED = 'transaction-payment-failed';
export const ERROR_CODE_CHARGE_ZERO_PAYIN = 'transaction-charge-zero-payin';
export const ERROR_CODE_CHARGE_ZERO_PAYOUT = 'transaction-charge-zero-payout';
export const ERROR_CODE_EMAIL_TAKEN = 'email-taken';
export const ERROR_CODE_EMAIL_NOT_FOUND = 'email-not-found';
export const ERROR_CODE_TOO_MANY_VERIFICATION_REQUESTS = 'email-too-many-verification-requests';
export const ERROR_CODE_UPLOAD_OVER_LIMIT = 'request-upload-over-limit';
export const ERROR_CODE_VALIDATION_INVALID_PARAMS = 'validation-invalid-params';
export const ERROR_CODE_VALIDATION_INVALID_VALUE = 'validation-invalid-value';
export const ERROR_CODE_NOT_FOUND = 'not-found';
export const ERROR_CODE_FORBIDDEN = 'forbidden';
export const ERROR_CODE_MISSING_STRIPE_ACCOUNT = 'transaction-missing-stripe-account';
// Vom eigenen initiate-privileged geworfen, wenn der Connect-Account des
// Gastgebers keine Überweisung annehmen kann (transfers-Capability inaktiv).
export const ERROR_CODE_CHARGE_DISABLED = 'transaction-charge-disabled';
export const ERROR_CODE_ANNUAL_PASS_VOUCHER_RESTRICTION = 'annual-pass-voucher-restrictions';
export const ERROR_CODE_VOUCHERIFY = 'voucherify-error';
// Beide aus der eigenen API: die Zahlung IST autorisiert, der Server bringt die
// Transaktion gerade selbst zu Ende (rescueAuthorizedPayment). Kein Fehlschlag —
// der Client wartet darauf, statt abzubrechen oder erneut zu buchen.
// abort-unpaid-transaction verweigert den Abbruch:
export const ERROR_CODE_TRANSACTION_AUTHORIZED_PENDING_RESCUE =
  'transaction-authorized-pending-rescue';
// initiate-privileged verweigert die Neubuchung derselben Nächte:
export const ERROR_CODE_BOOKING_PENDING_RESCUE = 'booking-pending-rescue';

const ERROR_CODES = [
  ERROR_CODE_TRANSACTION_LISTING_NOT_FOUND,
  ERROR_CODE_TRANSACTION_INVALID_TRANSITION,
  ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_CUSTOMER,
  ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_PROVIDER,
  ERROR_CODE_PAYMENT_FAILED,
  ERROR_CODE_CHARGE_ZERO_PAYIN,
  ERROR_CODE_CHARGE_ZERO_PAYOUT,
  ERROR_CODE_EMAIL_TAKEN,
  ERROR_CODE_EMAIL_NOT_FOUND,
  ERROR_CODE_TOO_MANY_VERIFICATION_REQUESTS,
  ERROR_CODE_UPLOAD_OVER_LIMIT,
  ERROR_CODE_VALIDATION_INVALID_PARAMS,
  ERROR_CODE_VALIDATION_INVALID_VALUE,
  ERROR_CODE_NOT_FOUND,
  ERROR_CODE_FORBIDDEN,
  ERROR_CODE_MISSING_STRIPE_ACCOUNT,
  ERROR_CODE_ANNUAL_PASS_VOUCHER_RESTRICTION,
];

// API error
// TODO this is likely to change soonish
propTypes.apiError = shape({
  id: propTypes.uuid.isRequired,
  status: number.isRequired,
  code: oneOf(ERROR_CODES).isRequired,
  title: string.isRequired,
  meta: object,
});

// Storable error prop type. (Error object should not be stored as it is.)
propTypes.error = shape({
  type: propTypes.value('error').isRequired,
  name: string.isRequired,
  message: string,
  status: number,
  statusText: string,
  apiErrors: arrayOf(propTypes.apiError),
});

// Options for showing just date or date and time on BookingTimeInfo and BookingBreakdown
export const DATE_TYPE_DATE = 'date';
export const DATE_TYPE_DATETIME = 'datetime';

propTypes.dateType = oneOf([DATE_TYPE_DATE, DATE_TYPE_DATETIME]);

const contentShape = shape({
  _uid: string.isRequired,
  component: string.isRequired,
  // Add any additional fields as needed
});

const parentShape = shape({
  id: number.isRequired,
  slug: string.isRequired,
  name: string.isRequired,
  disble_fe_editor: bool.isRequired,
  uuid: string.isRequired,
});

const previewTokenShape = shape({
  token: string.isRequired,
  timestamp: string.isRequired,
});

const lastAuthorShape = shape({
  id: number.isRequired,
  userid: string.isRequired,
  friendly_name: string.isRequired,
});

const translatedSlugShape = shape({
  lang: string.isRequired,
  slug: string.isRequired,
  name: string,
  published: bool,
});

const localizedPathShape = shape({
  path: string.isRequired,
  name: string,
  lang: string.isRequired,
  published: bool.isRequired,
});

propTypes.storyblokStory = shape({
  name: string.isRequired,
  parent_id: number.isRequired,
  group_id: string.isRequired,
  alternates: arrayOf(string).isRequired,
  created_at: string.isRequired,
  deleted_at: string,
  sort_by_date: string,
  tag_list: arrayOf(string).isRequired,
  updated_at: string.isRequired,
  published_at: string.isRequired,
  id: number.isRequired,
  uuid: string.isRequired,
  is_folder: bool.isRequired,
  content: contentShape.isRequired,
  published: bool.isRequired,
  slug: string.isRequired,
  path: string,
  full_slug: string.isRequired,
  default_root: string,
  disble_fe_editor: bool.isRequired,
  parent: parentShape.isRequired,
  is_startpage: bool.isRequired,
  unpublished_changes: bool.isRequired,
  meta_data: object,
  imported_at: string.isRequired,
  preview_token: previewTokenShape.isRequired,
  pinned: bool.isRequired,
  breadcrumbs: arrayOf(string).isRequired,
  first_published_at: string.isRequired,
  last_author: lastAuthorShape.isRequired,
  last_author_id: number.isRequired,
  translated_slugs: arrayOf(translatedSlugShape).isRequired,
  localized_paths: arrayOf(localizedPathShape).isRequired,
  position: number.isRequired,
  scheduled_dates: string.isRequired,
  favourite_for_user_ids: arrayOf(number).isRequired,
});

const meta_dataShape = shape({
  alt: string,
  title: string,
  source: string,
  copyright: string,
  alt__i18n__de: string,
});

const internal_tags_listShape = arrayOf(
  shape({
    id: number,
    name: string,
  }),
);

propTypes.storyblokImage = shape({
  id: number.isRequired,
  filename: string.isRequired,
  space_id: number,
  created_at: string,
  updated_at: string,
  file: any,
  asset_folder_id: any,
  deleted_at: any,
  short_filename: string,
  content_length: number,
  content_type: string,
  permanently_deleted: bool,
  alt: string,
  copyright: string,
  title: string,
  focus: string,
  ext_id: any,
  expire_at: string,
  source: string,
  internal_tag_ids: arrayOf(string),
  locked: bool,
  is_private: bool,
  publish_at: any,
  meta_data: meta_dataShape,
  internal_tags_list: internal_tags_listShape,
});

export { propTypes };
